import React from 'react';
import { faList, faPuzzlePiece, faTag } from '@fortawesome/pro-regular-svg-icons';
import { MembershipItem, MembershipItemType } from '@spike/membership-model';
import { MembershipItemsTableItem } from 'components/Memberships/Add/Steps/Items/Table/MembershipItemsTableItem';
import { createStyles, makeStyles, Theme, Typography } from '@material-ui/core';
import { Dropdown } from 'components/UI/V2/Dropdown/Dropdown';
import { useSelector } from 'react-redux';
import { v4 as uuid } from 'uuid';
import { RootState } from 'store';
import clsx from 'clsx';

interface MembershipItemsTableProps {
    items: MembershipItem[];
    onChange: (items: MembershipItem[]) => void;
    marketplaceID: number;
}

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        grid: {
            display: 'grid',
            gap: '16px 32px',
            gridTemplateColumns: 'minmax(90px, 1fr) 110px minmax(70px, 1fr)',

            [theme.breakpoints.up('md')]: {
                gap: 16,
                gridTemplateColumns: '1fr 120px 92px 75px'
            }
        },
        header: {
            paddingBottom: 16,
            borderBottom: 'solid 2px #000000',

            [theme.breakpoints.down('sm')]: {
                display: 'none'
            }
        },
        headerLabel: {
            'fontSize': 15,
            'lineHeight': 1.6,
            'fontWeight': 600,
            'color': '#000000',
            'textAlign': 'center',

            '&:first-child': {
                textAlign: 'left'
            },
            '&:last-child': {
                textAlign: 'right'
            }
        },
        dropdown: {
            display: 'block',

            [theme.breakpoints.down('sm')]: {
                marginTop: -32
            }
        },
        totalAmount: {
            'marginTop': 24,
            'fontSize': 15,
            'fontWeight': 400,
            'textAlign': 'right',

            [theme.breakpoints.down('sm')]: {
                marginTop: 32
            },

            '& b': {
                fontSize: 16,
                fontWeight: 600
            }
        }
    })
);

export const MembershipItemsTable: React.FC<MembershipItemsTableProps> = props => {
    const classes = useStyles();

    const servicesState = useSelector((state: RootState) => state.services);
    const productsState = useSelector((state: RootState) => state.products);
    const addOnServicesState = useSelector((state: RootState) => state.addOnServices);

    const dropdownOptions = React.useMemo(() => {
        const options = [];

        if (servicesState.services.length > 0) {
            options.push({
                icon: faList,
                label: 'Primary Service',
                value: MembershipItemType.SERVICE
            });
        }

        if (addOnServicesState.services.length > 0) {
            options.push({
                icon: faPuzzlePiece,
                label: 'Add-On Service',
                value: MembershipItemType.ADD_ON
            });
        }

        if (productsState.list.length > 0) {
            options.push({
                icon: faTag,
                label: 'Product',
                value: MembershipItemType.PRODUCT
            });
        }

        return options;
    }, [servicesState.services, productsState.list, addOnServicesState.services]);

    const totalMembershipPrice = React.useMemo(() => {
        return props.items.reduce((acc, item) => {
            if (item.unlimited) {
                return acc + item.amount;
            }

            return acc + item.amount * item.quantity;
        }, 0);
    }, [props.items]);

    const createMembershipItemHandler = (type: MembershipItemType) => {
        props.onChange([
            ...props.items,
            {
                id: 0,
                itemName: '',
                marketplaceID: props.marketplaceID,
                membershipID: 0,
                itemID: '',
                itemType: type,
                uuid: uuid(),
                quantity: 1,
                amount: 0,
                unlimited: false,
                total: '0.00'
            }
        ]);
    };

    const changeMembershipItemHandler = (item: MembershipItem) => {
        props.onChange(props.items.map(i => (i.uuid === item.uuid ? item : i)));
    };

    const deleteMembershipItemHandler = (uuid: string) => {
        props.onChange(props.items.filter(i => i.uuid !== uuid));
    };

    return (
        <div>
            <div className={clsx(classes.grid, classes.header)}>
                <div className={classes.headerLabel}>Item</div>
                <div className={classes.headerLabel}>Qty</div>
                <div className={classes.headerLabel}>Price</div>
                <div className={classes.headerLabel}>Total</div>
            </div>

            <div>
                {props.items.map(item => (
                    <MembershipItemsTableItem
                        key={item.uuid}
                        item={item}
                        className={classes.grid}
                        onChange={changeMembershipItemHandler}
                        onDelete={deleteMembershipItemHandler}
                        currentItems={props.items}
                    />
                ))}
            </div>

            <Typography className={classes.totalAmount}>
                {totalMembershipPrice > 0 ? (
                    <>
                        <b>Total</b>&nbsp;&nbsp;${totalMembershipPrice.toFixed(2)}
                    </>
                ) : (
                    <span>&nbsp;</span>
                )}
            </Typography>

            <Dropdown
                label="Add Item"
                options={dropdownOptions}
                className={classes.dropdown}
                onSelect={createMembershipItemHandler}
            />
        </div>
    );
};
